import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, input, model, output, signal, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { UiModule } from '@components/ui/ui-module';
import { environment } from '@environments/environment';
import { Order } from '@models/order.model';
import { PaymentService, StripePaymentIntent } from '@services/payment.service';
import { Router } from '@services/route.service';
import { Observable, of, switchMap, tap } from 'rxjs';

@Component({
  selector: 'lgb-payment-method',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    UiModule
  ],
  templateUrl: './payment-method.html',
  styleUrl: './payment-method.css',
})
export class PaymentMethod {
  //service
  readonly router=inject(Router);
  readonly paymentS=inject(PaymentService);

  //input/ouput
  readonly amount=input<number>(null);                  //montant que l'on doit afficher sur le bouton de paiement
  readonly payLoading=model(false);                     //model permettant de savoir quand est-ce que le composant est en train de faire un loading
  readonly order$=input.required<Observable<Order>>();  //observable pour récuperer/créer la commande
  readonly error=output<string>();                      //emit error en cas d'erreur
  private order: Order;
  private stripePayment: StripePaymentIntent;

  //ui
  readonly method=signal<string>(null);
  readonly method$=toObservable(this.method);
  readonly stripeDiv=viewChild<ElementRef<HTMLDivElement>>('stripeForm');
  private stripe: any;
  private elements: any;

  constructor(){
    this.method$.subscribe(method => {
      if(method=='cb')
        this.initStripe();
    });
  }

  //création order + paymentIntent si non existant
  initStripe(){
    this.payLoading.set(true);
    //commande
    let obs$: Observable<Order>=this.order$()
      .pipe(
        tap(order => this.order=order)
      );
    if(this.order)
      obs$=of(this.order);

    //paymentIntent
    obs$.pipe(
      switchMap(order => ((this.stripePayment)? of(this.stripePayment) : this.paymentS.payStripeOrder(order.ident)))
    )
    .subscribe({
      next : intent => {
        this.stripePayment=intent;
        this.paymentS.loadStripeJs()
          .then((Stripe) => {
            this.displayStripe(Stripe);
            this.payLoading.set(false);
          });
      },
      error: e => this.error.emit(e)
    });
  }

  displayStripe(Stripe: any){
    this.stripe=Stripe(environment.stripKey);
    this.elements=this.stripe.elements({clientSecret: this.stripePayment.clientSecret});

    const options={
      layout: {
        type: 'accordion',
        defaultCollapsed: false,
        radios: 'always',
        spacedAccordionItems: false
      },
      wallets: {
        applePay: 'never',
        googlePay: 'never',
      }
    };

    const paymentElement=this.elements.create('payment', options);
    paymentElement.mount(this.stripeDiv().nativeElement);
  }

  //méthode qui lance le paiement
  //commence par récuperer/créer la commande via l'observable
  //lance le paiement vers la plateforme choisie
  pay(){
    switch(this.method()){
      case 'cb':
        this.payStripe();
        break;
      case 'paypal':
        this.payPaypal();
        break;
      default:
        break;
    }
  }

  payPaypal(){
    this.payLoading.set(true);
    this.order$()
      .pipe(
        tap(order => this.order=order),
        switchMap(() => this.paymentS.payPaypalOrder(this.order.ident))
      )
      .subscribe({
        next: subscription => window.location=subscription.url,
        error: e => {
          this.payLoading.set(false);
          this.error.emit(e);
        }
      });
  }

  payStripe() {
    this.payLoading.set(true);

    this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: `${window.location.origin}/${this.router.get('payment-failed')}`,
      },
      redirect: 'if_required',
    }).then((res: any) => {
        if(res.paymentIntent){
          if(res.paymentIntent.status=='succeeded')
            this.router.navigate('payment-success');
          else
            this.router.navigate('payment-failed');
        }
        else
          this.payLoading.set(false);
      },
      (error: any) => {
      if(error){
        this.error.emit(error.message ?? 'Paiement échoué');
        this.payLoading.set(false);
      }
    });
    // si redirect nécessaire, Stripe redirige -> pas besoin de set payLoading(false)
  }
}